import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { citiesData } from '../utils/cityData';

const LocationAutocomplete = ({ onSelect, initialValue }) => {
    const [inputValue, setInputValue] = useState(initialValue || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const wrapperRef = useRef(null);

    // Set initial city if provided
    useEffect(() => {
        if (initialValue && !inputValue) {
            setInputValue(initialValue);
        }
    }, [initialValue]);

    // Filter cities based on input value
    const getSuggestions = (input) => {
        if (!input) return [];

        setIsSearching(true);

        // Simulate API delay
        setTimeout(() => {
            const inputLower = input.toLowerCase();
            const filteredSuggestions = citiesData.filter(city =>
                city.name.toLowerCase().includes(inputLower)
            ).slice(0, 5); // Limit to 5 suggestions

            setSuggestions(filteredSuggestions);
            setIsSearching(false);
        }, 300);
    };

    // Handle input change
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);

        if (value.length > 1) {
            getSuggestions(value);
            setShowSuggestions(true);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
            setIsSearching(false);
        }
    };

    // Handle suggestion selection
    const handleSuggestionClick = (city) => {
        setInputValue(city.name);
        setSuggestions([]);
        setShowSuggestions(false);

        // Pass coordinates to parent component in "lat,lng" format
        onSelect(`${city.lat},${city.lng}`, city.name);
    };

    // Clear the input
    const handleClearInput = () => {
        setInputValue('');
        setSuggestions([]);
        setShowSuggestions(false);
        setIsSearching(false);

        // Clear the selected location in parent component
        onSelect('', '');
    };

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [wrapperRef]);

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" className="text-#A961ED" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                </div>

                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder="Enter a city name"
                    onFocus={() => inputValue.length > 1 && setShowSuggestions(true)}
                    className="w-full py-2 pl-8 pr-8 rounded-lg bg-transparent border border-white focus:border-accent focus:outline-none text-[14px] font-light mb-0"
                />

                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                    {isSearching && (
                        <div className="text-accent mr-2 animate-spin">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </div>
                    )}

                    {inputValue && (
                        <button
                            onClick={handleClearInput}
                            className="text-gray-400 hover:text-white transition-colors focus:outline-none"
                            aria-label="Clear input"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {showSuggestions && (
                <div className="absolute mt-1 w-full rounded-lg bg-gray-900 border border-gray-700 shadow-lg overflow-hidden z-10">
                    <ul className="max-h-48 py-1 overflow-auto text-sm">
                        {suggestions.length > 0 ? (
                            suggestions.map((city, index) => (
                                <li
                                    key={index}
                                    onClick={() => handleSuggestionClick(city)}
                                    className="px-3 py-1.5 flex items-center hover:bg-gray-800 cursor-pointer transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" className="text-#A961ED mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span className="text-white text-sm">{city.name}</span>
                                </li>
                            ))
                        ) : (
                            <li className="px-3 py-2 text-gray-400 text-center text-sm">
                                {isSearching ? 'Searching...' : 'No cities found. Try a different search.'}
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default LocationAutocomplete; 